import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import ServicePlanWorkflowType, {
  SERVICE_PLAN_PAYMENT_METHOD,
  SERVICE_PLAN_PAYMENT_PERIOD,
} from '../../../utility/types/ServicePlanWorkflowTypes';

const now = new Date();
const DEFAULT_EXPIRATION_MONTH = String(now.getMonth() + 1).padStart(2, '0');
const DEFAULT_EXPIRATION_YEAR = String(now.getFullYear());

export const selectServicePlanWorkflowState = (state) => state.servicePlanWorkflow;
export const selectCurrentWorkflowStepIndex = (state) => state
  .servicePlanWorkflow.currentWorkflowStepIndex;
export const selectSelectedServicePlan = (state) => state.servicePlanWorkflow.selectedServicePlan;

export const selectCurrentWorkflowDefinition = createSelector(
  selectServicePlanWorkflowState,
  (wf) => {
    if (!wf || !wf.currentWorkflow) return ServicePlanWorkflowType.NEWSUBSCRIPTION;
    if (typeof wf.currentWorkflow === 'string') {
      return ServicePlanWorkflowType[wf.currentWorkflow]
            || ServicePlanWorkflowType.NEWSUBSCRIPTION;
    }
    return wf.currentWorkflow;
  },
);

export const selectIsServicePlanWorkflowSubmitting = createSelector(
  selectServicePlanWorkflowState,
  (wf) => wf.submitting,
);

export const selectServicePlanWorkflowErrorList = createSelector(
  selectServicePlanWorkflowState,
  (state) => state.errorList,
);

const getPaymentMethodForApi = (method) => {
  switch (method) {
    case SERVICE_PLAN_PAYMENT_METHOD.CREDIT_CARD_ON_FILE:
      return 'credit-card-existing';
    case SERVICE_PLAN_PAYMENT_METHOD.CHECK_OR_BANK_TRANSFER:
      return 'purchase-order';
    case SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD:
    default:
      return 'credit-card-new';
  }
};

const buildServicePlanWorkflowPayload = (state) => {
  const {
    selectedServicePlan,
    serviceSelection,
    orderSummary,
    paymentMethod,
  } = state.servicePlanWorkflow;

  const apiPaymentMethod = getPaymentMethodForApi(paymentMethod.method);

  return {
    subscriptionTierId: selectedServicePlan ? selectedServicePlan.id : null,
    recurringInterval: serviceSelection.selectedBillingCycle === SERVICE_PLAN_PAYMENT_PERIOD.YEARLY
      ? 'year'
      : 'month',
    prepaidServicePeriodCount: serviceSelection.additionalYears,
    paymentMethod: apiPaymentMethod,
    cardNumber: apiPaymentMethod === 'credit-card-new' ? paymentMethod.cardNumber : '',
    cardExpirationYear: apiPaymentMethod === 'credit-card-new' ? String(paymentMethod.expirationYear).slice(-2) : '',
    cardExpirationMonth: apiPaymentMethod === 'credit-card-new' ? String(paymentMethod.expirationMonth).padStart(2, '0') : '',
    cardCvv: apiPaymentMethod === 'credit-card-new' ? paymentMethod.cvv : '',
    purchaseOrderNumber: apiPaymentMethod === 'purchase-order'
      ? orderSummary.purchaseOrderNumber
      : '',
    promoCode: orderSummary.promoCode || '',
    keepCardOnFile: paymentMethod.method === SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD ? 'true' : 'false',
    primaryPrice: orderSummary.primaryPrice || 0,
    extraPrice: orderSummary.extraPrice || 0,
    pricingSubtotal: orderSummary.pricingSubtotal || 0,
    creditAmount: orderSummary.creditAmount || 0,
    subtotal: orderSummary.subtotal || 0,
    taxAmount: orderSummary.taxAmount || 0,
    grandTotal: orderSummary.totalAmount || 0,
  };
};

const buildPayBalanceWorkflowPayload = (state) => {
  const { orderSummary, paymentMethod } = state.servicePlanWorkflow;

  return {
    paymentMethod: getPaymentMethodForApi(paymentMethod.method),
    cardNumber: paymentMethod.cardNumber,
    cardExpirationYear: String(paymentMethod.expirationYear).slice(-2),
    cardExpirationMonth: String(paymentMethod.expirationMonth).padStart(2, '0'),
    cardCvv: paymentMethod.cvv,
    keepCardOnFile: paymentMethod.method === SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD ? 'true' : 'false',
    primaryPrice: orderSummary.primaryPrice || 0,
    extraPrice: orderSummary.extraPrice || 0,
    pricingSubtotal: orderSummary.pricingSubtotal || 0,
    creditAmount: orderSummary.creditAmount || 0,
    subtotal: orderSummary.subtotal || 0,
    taxAmount: orderSummary.taxAmount || 0,
    grandTotal: orderSummary.totalAmount || 0,
  };
};

const getServicePlanWorkflowRequest = (workflow, state) => {
  switch (workflow.id) {
    case ServicePlanWorkflowType.PAYBALANCE.id:
      return {
        url: '/subscription/billing/data/pay-amount-due',
        payload: buildPayBalanceWorkflowPayload(state),
      };

    case ServicePlanWorkflowType.PAYINADVANCE.id:
      return {
        url: '/subscription/billing/data/renew',
        payload: buildServicePlanWorkflowPayload(state),
      };

    default:
      return {
        url: '/subscription/billing/data/upgrade',
        payload: buildServicePlanWorkflowPayload(state),
      };
  }
};

export const submitServicePlanWorkflow = createAsyncThunk(
  'servicePlanWorkflow/submitServicePlanWorkflow',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const workflow = selectCurrentWorkflowDefinition(state);
    const { url, payload } = getServicePlanWorkflowRequest(workflow, state);

    try {
      const response = await axios.post(url, qs.stringify(payload), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        withCredentials: true,
      });

      if (response.data && response.data.success === false) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data
          ? error.response.data
          : error.message,
      );
    }
  },
);

const initialState = {
  currentWorkflow: 'NEWSUBSCRIPTION',
  currentWorkflowStepIndex: 0,
  error: null,
  errorList: [],
  submitting: false,
  currentServicePlan: null,
  selectedServicePlan: null,
  servicePlanDiscount: 0,
  serviceSelection: {
    additionalYears: 0,
    selectedBillingCycle: SERVICE_PLAN_PAYMENT_PERIOD.MONTHLY,
  },
  orderSummary: {
    purchaseOrderNumber: '',
    primaryPrice: 0,
    extraPrice: 0,
    pricingSubtotal: 0,
    creditAmount: 0,
    subtotal: 0,
    taxAmount: 0,
    totalAmount: 0,
  },
  paymentMethod: {
    method: SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD,
    cardNumber: '',
    expirationMonth: DEFAULT_EXPIRATION_MONTH,
    expirationYear: DEFAULT_EXPIRATION_YEAR,
    cvv: '',
  },
};

export const servicePlanWorkflowSlice = createSlice({
  name: 'servicePlanWorkflow',
  initialState,
  reducers: {
    clearServicePlanWorkflowSlice: () => initialState,
    setCurrentWorkflow: (state, action) => {
      state.currentWorkflow = action.payload;
      state.currentWorkflowStepIndex = 0;
    },
    nextStep: (state) => {
      state.currentWorkflowStepIndex += 1;
    },
    previousStep: (state) => {
      if (state.currentWorkflowStepIndex > 0) {
        state.currentWorkflowStepIndex -= 1;
      }
    },
    setStepIndex: (state, action) => {
      state.currentWorkflowStepIndex = action.payload;
    },
    updateSingleField: (state, action) => {
      const { field, value } = action.payload;
      const path = Array.isArray(field) ? field : String(field).split('.');

      let target = state;
      for (let i = 0; i < path.length - 1; i += 1) {
        const key = path[i];
        if (target[key] === undefined || target[key] === null) {
          target[key] = {};
        }
        target = target[key];
      }
      target[path[path.length - 1]] = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitServicePlanWorkflow.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.errorList = [];
      })
      .addCase(submitServicePlanWorkflow.fulfilled, (state) => {
        state.submitting = false;
        state.error = null;
        state.errorList = [];
      })
      .addCase(submitServicePlanWorkflow.rejected, (state, action) => {
        const fallbackErrorMessage = action.error && action.error.message
          ? action.error.message
          : 'An unexpected error occurred, please try again.';

        state.submitting = false;
        state.error = action.payload || fallbackErrorMessage;
        state.errorList = action.payload && Array.isArray(action.payload.data)
          ? action.payload.data
          : [];
      });
  },
});

const { actions, reducer } = servicePlanWorkflowSlice;
export const {
  clearServicePlanWorkflowSlice,
  setCurrentWorkflow,
  nextStep,
  previousStep,
  setStepIndex,
  updateSingleField,
} = actions;

export default reducer;
